import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { STUDY_LANDSCAPES, type LandscapeId } from "@/features/story/data/landscapes";
import { landCoverQueryForLandscape } from "@/features/story/data/services";
import {
  HUB_PERIODS,
  hubPeriodsForLandscape,
  type HubLanguage,
  type HubPeriod,
  type HubPeriodId,
} from "./hub-scope";

type HubScopeValue = {
  readonly landscapeId: LandscapeId;
  readonly setLandscapeId: (id: LandscapeId) => void;
  readonly landscapeLabel: string;
  readonly territoryId: string;
  readonly periodId: HubPeriodId;
  readonly setPeriodId: (id: HubPeriodId) => void;
  readonly period: HubPeriod;
  readonly language: HubLanguage;
  readonly setLanguage: (lng: HubLanguage) => void;
  readonly queryVars: {
    territoryIds: string[];
    dateFrom: string;
    dateTo: string;
    metric: "superficie" | "part";
    erosionIndicateurId: string;
    ecoFrom: string;
    ecoTo: string;
    ecoCarboneId: string;
    ecoPfnlId: string;
    ecoEauId: string;
  };
};

const HubScopeContext = createContext<HubScopeValue | null>(null);

export function HubScopeProvider({ children }: { readonly children: ReactNode }) {
  const { i18n } = useTranslation();
  const [landscapeId, setLandscapeIdState] = useState<LandscapeId>("madina-oula");
  const [periodId, setPeriodIdState] = useState<HubPeriodId>("2005-2025");

  const language: HubLanguage = i18n.language?.startsWith("en") ? "en" : "fr";

  const setLandscapeId = useCallback((id: LandscapeId) => {
    setLandscapeIdState(id);
    setPeriodIdState((current) => {
      const allowed = hubPeriodsForLandscape(id);
      return allowed.some((period) => period.id === current)
        ? current
        : (allowed[0]?.id ?? "2005-2025");
    });
  }, []);

  const setPeriodId = useCallback((id: HubPeriodId) => {
    setPeriodIdState(id);
  }, []);

  const setLanguage = useCallback(
    (lng: HubLanguage) => {
      void i18n.changeLanguage(lng);
    },
    [i18n],
  );

  const value = useMemo<HubScopeValue>(() => {
    const place = STUDY_LANDSCAPES.find((p) => p.id === landscapeId) ?? STUDY_LANDSCAPES[2]!;
    const period = HUB_PERIODS.find((p) => p.id === periodId) ?? HUB_PERIODS[0]!;
    const landCoverQ = landCoverQueryForLandscape(place.id);
    return {
      landscapeId: place.id,
      setLandscapeId,
      landscapeLabel: place.shortLabel,
      territoryId: landCoverQ.territoryId,
      periodId: period.id,
      setPeriodId,
      period,
      language,
      setLanguage,
      queryVars: {
        territoryIds: [landCoverQ.territoryId],
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        metric: landCoverQ.metric,
        erosionIndicateurId: "nbs-ind-distribution-erosion",
        ecoFrom: "2014-01-01",
        ecoTo: "2014-12-31",
        ecoCarboneId: "nbs-ind-eco-regulation-climat-global",
        ecoPfnlId: "nbs-ind-eco-pfnl-total",
        ecoEauId: "nbs-ind-eco-eau-autoconsommation",
      },
    };
  }, [landscapeId, periodId, language, setLandscapeId, setPeriodId, setLanguage]);

  return <HubScopeContext.Provider value={value}>{children}</HubScopeContext.Provider>;
}

export function useHubScope(): HubScopeValue {
  const ctx = useContext(HubScopeContext);
  if (!ctx) {
    throw new Error("useHubScope must be used within HubScopeProvider");
  }
  return ctx;
}
